import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { AdminPageHeader } from '../../Admin';
import { Table } from '../../shared';
import { getAllDigitalDelivery, getLocalStorageItem, popup, setLocalStorageItem } from '../../../utils';
import { usePagination } from '../../../hooks';
import {
    DIGITAL_SAFETY_LIBRARY,
    DIGITAL_SAFETY_LIBRARY_CUSTOMER_COLUMNS,
    LOCAL_STORAGE,
    QUERY_KEYS,
} from '../../../constants';
import { useDebouncedValue } from '../../../hooks/useDebounceValue';

export const DigitalSafetyLibraryCustomer = () => {
    const [response, setResponse] = useState({
        data: [],
        count: 1,
    });
    const accountId = getLocalStorageItem(LOCAL_STORAGE.CUSTOMER_ID);
    const pagination = usePagination(response.data.length, response.count);
    const [search, setSearch] = useState('');

    const [debounceSearch] = useDebouncedValue(search, 500);
    const { data, isFetching, isFetched, isError, error } = useQuery({
        queryKey: [
            QUERY_KEYS.DIGITAL_SAFETY_MANUAL,
            accountId,
            debounceSearch,
            pagination.limit,
            pagination.offset,
        ],
        queryFn: async () =>
            await getAllDigitalDelivery({
                account_id: accountId,
                limit: pagination.limit,
                offset: pagination.offset,
                search: debounceSearch,
            }),
        select: (data) => ({ data: data.data, count: data.count }),
        placeholderData: { data: [], count: 0 },
        enabled: !!accountId,
    });

    useEffect(() => {
        if (isFetched && data?.data) {
            setResponse({ data: data.data, count: data.count });
        }
    }, [data, isFetched]);

    const prepareAssistantContext = () => {
        try {
            const hostAccountId = getLocalStorageItem(LOCAL_STORAGE.ACCOUNT_ID);
            if (!hostAccountId) return;
            const total = response.count || 0;
            const payload = {
                type: 'digital_safety_library',
                accountId: hostAccountId,
                totalDeliverables: total,
            };
            setLocalStorageItem('ssc_ai_selection', payload);
        } catch (e) {
            // best-effort; do not impact library UX
        }
    };

    if (isError) popup('error', error.message);

    return (
        <>
            <AdminPageHeader
                heading={DIGITAL_SAFETY_LIBRARY.PAGE_HEADING}
                backbutton
                showSearchBar
                searchBarPlaceholder={DIGITAL_SAFETY_LIBRARY.FILE_NAME_SEARCH}
                count={pagination.dataCount}
                setSearch={setSearch}
            />

            <div className='d-flex justify-content-end mb-2 px-1 px-lg-0'>
                <button
                    type='button'
                    className='ssc-card-btn2 text-nowrap'
                    onClick={prepareAssistantContext}
                    title='Prime the AI assistant with this account&apos;s digital safety library. Then open the Assistant to review files and next actions.'
                >
                    Ask Assistant
                </button>
            </div>

            <Table
                columns={DIGITAL_SAFETY_LIBRARY_CUSTOMER_COLUMNS}
                data={response.data}
                pagination={pagination}
                pageName={DIGITAL_SAFETY_LIBRARY.PAGE_HEADING}
                stickyColumns={['File Name']}
                isLoading={isFetching}
            />
        </>
    );
};
